import { FC, useState } from 'react';

import {
    AccountSparkBrand,
    BrandTagState,
    GetAccountVendorRetailerPosDataResponseBody,
} from '@sparkplug/lib';

import LinkLabel from '@features/account-links/components/LinkLabel';
import { getMissingTagsCount } from '@features/product-tags/utils';

import { AlertIcon } from '@components/icons';
import Skeleton from '@components/layout/Skeleton';
import Tabs from '@components/layout/Tabs';

import './BrandTabs.scss';

export interface BrandTabsProps {
    selectedBrandId: string;
    updateSelectedBrand: (updatedBrandId: string) => void;
    vendorRetailerPosData?: GetAccountVendorRetailerPosDataResponseBody;
    brands: AccountSparkBrand[];
    vendorRetailerPosDataIsReady: boolean;
    brandsAreReady: boolean;
}

export const formattedTagState = (missing: number, tagState?: BrandTagState) => {
    if (!tagState) return '';

    const textColorClass = tagState?.state === 'missing-tags' ? 'missing-products-label' : '';

    const mapForText = {
        'all-tagged': 'All products tagged',
        'missing-tags': (
            <>
                <AlertIcon />
                {missing} missing tags
            </>
        ),
        'none-mapped': 'No products mapped',
    };

    return (
        <p className={textColorClass}>
            {missing === 0 && tagState.state !== 'none-mapped'
                ? mapForText['all-tagged']
                : mapForText[tagState.state]}
        </p>
    );
};

const BrandTabs: FC<BrandTabsProps> = ({
    brands,
    selectedBrandId,
    updateSelectedBrand,
    brandsAreReady,
    vendorRetailerPosDataIsReady,
    vendorRetailerPosData,
}) => {
    const [selected, setSelected] = useState(selectedBrandId);

    const handleTabClick = (brandId: string) => {
        setSelected(brandId);
        updateSelectedBrand(brandId);
    };

    const getProducts = (brandId: string) => {
        return vendorRetailerPosData?.productsVendorBrandMap?.[brandId ?? '']?.products ?? [];
    };

    return (
        <div className="brand-tabs">
            <h4>Your Brands</h4>
            {brandsAreReady && brands?.length ? (
                <Tabs value={selected ?? brands?.[0]?._id} color="blue" orientation="vertical">
                    {brands.map(({ _id, photo, name, isEnabled, tagState }) => (
                        <Tabs.Tab
                            onClick={() => handleTabClick(_id)}
                            key={_id}
                            label={
                                <LinkLabel
                                    imageUrl={photo}
                                    title={name}
                                    subtitle={
                                        vendorRetailerPosDataIsReady ? (
                                            formattedTagState(
                                                getMissingTagsCount(getProducts(_id)),
                                                tagState,
                                            )
                                        ) : (
                                            <Skeleton height={16} />
                                        )
                                    }
                                />
                            }
                            disabled={!isEnabled}
                            value={_id}
                        />
                    ))}
                </Tabs>
            ) : (
                <Skeleton height={300} />
            )}
        </div>
    );
};

export default BrandTabs;
